import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { Link } from 'site-modules/shared/components/link/link';
import { InventoryEntities } from 'client/data/models/inventory';
import { ReviewsModel, ReviewEntities } from 'client/data/models/dealer-reviews';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import {
  getDealershipStateCityNameUrl,
  getDealershipWriteReviewLink,
} from 'site-modules/shared/utils/dealership-link-constructor';
import { REVIEWS_SALES } from 'client/constants/dealer-reviews';
import { DealerReviewsList } from './dealer-reviews-list';

import './dealer-reviews.scss';

const REVIEWS_TO_SHOW = 3;

/**
 * Shows dealer sales reviews with overall rating.
 * @param vinData
 * @param overallRating
 * @param salesReviews
 * @param reviewsToShow
 * @param sectionTitle
 * @returns {ReactElement}
 */
export function DealerReviewsUI({
  vinData = {},
  overallRating = {},
  salesReviews = {},
  reviewsToShow = REVIEWS_TO_SHOW,
  sectionTitle = 'Customer Reviews',
  isMobile = false,
}) {
  const reviews = get(salesReviews, 'newReviews', []);
  const address = get(vinData, 'dealerInfo.address', {});
  const rooftopLogicalName = get(vinData, 'dealerInfo.rooftopLogicalName', '');
  // We want to display count of new (max 2 years old) reviews only,
  // so cannot use totalCount field from the API which contains total of old + new reviews
  const totalCount = get(overallRating, 'salesReview.totalCount', 0);
  const reviewsLength = reviews.length;
  const dealershipLink = getDealershipStateCityNameUrl({
    state: address.stateName,
    city: address.city,
    dealership: rooftopLogicalName,
  });
  const writeReviewLink = getDealershipWriteReviewLink({
    state: address.stateName,
    city: address.city,
    dealership: rooftopLogicalName,
    reviewType: REVIEWS_SALES,
  });
  const emptyReview = (
    <div>
      <div className="text-gray-darker mb-0_75">Be the first to write a review for this dealership!</div>
      <div>
        <a
          href={writeReviewLink}
          className="dealer-review-cta text-primary-darker"
          data-tracking-id="ddp_go_to_review_form"
        >
          Write a sales review
          <i className="icon-arrow-right4 small text-primary-darker ms-0_25" aria-hidden />
        </a>
      </div>
    </div>
  );
  const isEmptyView = !(totalCount && reviewsLength);

  return (
    <ContentSection
      className="customer-reviews"
      sectionTitle={sectionTitle}
      dataTrackingParent="edm-entry-customer-reviews"
      titleClassName={classNames('section-title heading-4', {
        'mb-1_5': !isEmptyView,
        'mb-0_5': isEmptyView,
      })}
      line={false}
    >
      {totalCount && reviewsLength ? (
        <Fragment>
          <DealerReviewsList reviews={reviews} max={reviewsToShow} isMobile={isMobile} />
          <Link to={dealershipLink} className="dealer-review-cta text-primary-darker">
            <span>See all dealership reviews</span>
            <i className="icon-arrow-right4 small text-primary-darker ms-0_25" aria-hidden />
          </Link>
        </Fragment>
      ) : (
        emptyReview
      )}
    </ContentSection>
  );
}

DealerReviewsUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  overallRating: ReviewEntities.OverallRatingEntity,
  salesReviews: ReviewEntities.ReviewsByType,
  reviewsToShow: PropTypes.number,
  sectionTitle: PropTypes.string,
  isMobile: PropTypes.bool,
};

export const stateToPropsConfig = {
  overallRating: bindToPath(
    ({
      vinData: {
        dealerInfo: { rooftopId },
      },
    }) => `dealershipReviews.id["${rooftopId}"].overallRating`,
    ReviewsModel
  ),
  salesReviews: bindToPath(
    ({
      vinData: {
        dealerInfo: { rooftopId },
      },
    }) => `dealershipReviews.id["${rooftopId}"].sales`,
    ReviewsModel
  ),
};

export const DealerReviews = connectToModel(DealerReviewsUI, stateToPropsConfig);
